import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Table, { TableColumn } from '../../../../components/organisms/table';
import MVCHeader from '../../../../components/organisms/mvc-header';
import Pagination, {
	PaginationProps,
} from '../../../../components/organisms/pagination';
import Actions from '../../../../components/molecules/actions';
import { ModalProps } from '../../../../components/template/mvc-modal';
import classNames from 'classnames';
import UserService, {
	IUser,
	UserPermissions,
} from '../../../../services/User.service';
import Text from '../../../../components/atoms/text';
import UsersModal from './modal';
import { NotificationToast } from '../../../../components/organisms/notification-toast';
import ModalConfirmation from '../../../../components/organisms/modal-confirmation';
import SVGIcon from '../../../../components/atoms/icons';
import { useRouteLoaderData } from 'react-router-dom';
import { HomeLoaderData } from '../../../../types/homeLoaderData';

const initialState: IUser = {
	name: '',
	email: '',
	cpf: '',
	phone: '',
	role: '',
	client: {},
	yards: [],
	isActive: true,
	shouldReceiveAlerts: false,
	acessoFluxoUnico: false,
	permissions: {
		isMaintenance: false,
		isMonitoring: false,
		isMovement: false,
		isReport: false,
		isLocalAdmin: false,
		isSystemAdmin: false,
	},
};

const Users = () => {
	const { t } = useTranslation();
	const { accountId } = useRouteLoaderData('home') as HomeLoaderData;

	const modalTitles = {
		create: t('admin.users.new'),
		edit: t('admin.users.edit'),
		view: t('admin.users.view'),
	};

	const [data, setData] = useState<IUser[]>([]);
	const [isDeleteModalVisible, setIsDeleteModalVisible] =
		useState<boolean>(false);
	const [dataToDelete, setDataToDelete] = useState<IUser | null>(null);

	const [paginationOptions, setPaginationOptions] = useState<PaginationProps>(
		{
			pageCurrent: 1,
			pageSize: 10,
			totalItems: 0,
			totalPages: 1,
		}
	);

	const [modalProps, setModalProps] = useState<ModalProps<IUser>>({
		data: initialState,
		title: modalTitles.view,
		mode: 'view',
		isModalVisible: false,
		onClose: (reload?: boolean) => {
			setModalProps((prev) => ({
				...prev,
				isModalVisible: false,
				data: initialState,
			}));
			if (reload) fetchData();
		},
	});

	const permissionsName: { [key in keyof UserPermissions]: string } = {
		isSystemAdmin: t('admin.system_admin'),
		isLocalAdmin: t('admin.local_admin'),
		isMonitoring: t('monitor.title'),
		isMovement: t('movement.title'),
		isMaintenance: t('maintenance.title'),
		isReport: t('reports.title'),
	};

	const fetchData = useCallback(async () => {
		const response = await UserService.getAll({
			pageCurrent: paginationOptions.pageCurrent,
			pageSize: paginationOptions.pageSize,
			idClient: Number(accountId),
		});
		if (response) {
			setData(response.data);
			setPaginationOptions((prev) => ({
				...prev,
				totalItems: response.totalItems,
				totalPages: response.totalPages,
			}));
		}
	}, [paginationOptions.pageCurrent, paginationOptions.pageSize]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const onPaginationUpdate = (pageSize: number, pageCurrent: number) => {
		setPaginationOptions((prev) => ({ ...prev, pageSize, pageCurrent }));
	};

	const onActionsViewClick = useCallback(
		(data: IUser) => {
			setModalProps((prev) => ({
				...prev,
				title: modalTitles.view,
				mode: 'view',
				isModalVisible: true,
				data,
			}));
		},
		[modalTitles.view]
	);

	const onActionsEditClick = useCallback(
		(data: IUser) => {
			setModalProps((prev) => ({
				...prev,
				title: modalTitles.edit,
				mode: 'edit',
				isModalVisible: true,
				data,
			}));
		},
		[modalTitles.edit]
	);

	const onActionsDeleteClick = (data: IUser) => {
		setDataToDelete(data);
		setIsDeleteModalVisible(true);
	};

	const onDeleteModalClose = () => {
		setIsDeleteModalVisible(false);
		setDataToDelete(null);
	};

	const onDeleteConfirm = async () => {
		if (!dataToDelete || !dataToDelete.id) return;
		await UserService.delete(dataToDelete.id);
		fetchData();
		NotificationToast.Success(
			`${t('admin.user')} ${t('alert.success.delete')}`
		);
		onDeleteModalClose();
	};

	const onCreateClick = useCallback(() => {
		setModalProps((prev) => ({
			...prev,
			title: modalTitles.create,
			mode: 'create',
			isModalVisible: true,
			data: initialState,
		}));
	}, [modalTitles.create]);

	const getTruePermissions = (obj: IUser): string => {
		const truePermissions = (
			Object.entries(obj.permissions) as [
				keyof UserPermissions,
				boolean,
			][]
		)
			.filter(([key, value]) => value && permissionsName[key])
			.map(([key]) => permissionsName[key]);

		return truePermissions.join(', ');
	};

	const getYards = (obj: IUser): string => {
		const yards = obj.yards?.map((item) => item.name);
		return yards?.join(', ');
	};

	const columns: TableColumn<IUser>[] = [
		{ key: 'name', label: t('misc.name') },
		{ key: 'email', label: t('misc.email') },
		{ key: 'cpf', label: t('admin.users.cpf') },
		{ key: 'phone', label: t('misc.phone') },
		{
			key: 'permissions',
			label: t('admin.permissions'),
			children: (row) => (
				<Text className="text-xs">{getTruePermissions(row)}</Text>
			),
		},
		{
			key: 'yards',
			label: t('port_management.yards.title'),
			children: (row) => <Text className="text-xs">{getYards(row)}</Text>,
		},
		{
			key: 'actions',
			label: t('misc.actions'),
			children: (row) => (
				<Actions
					onViewClick={() => onActionsViewClick(row)}
					onEditClick={() => onActionsEditClick(row)}
					onDeleteClick={() => onActionsDeleteClick(row)}
				/>
			),
		},
	];

	return (
		<>
			<div className="flex-grow grid grid-rows-[auto_1fr_auto] p-4 gap-y-4">
				<MVCHeader
					title={t('admin.users.list')}
					onCreateText={t('admin.users.new')}
					onCreateClick={onCreateClick}
				/>

				<div className="overflow-x-auto w-full">
					<Table
						data={data}
						columns={columns}
						className={classNames(
							'grid-cols-[1fr_1fr_auto_auto_1fr_1fr_auto]'
						)}
					/>
				</div>

				<Pagination
					{...paginationOptions}
					onUpdate={onPaginationUpdate}
				/>
			</div>

			<UsersModal
				setModalProps={setModalProps}
				permissionsName={permissionsName}
				{...modalProps}
			/>
			<ModalConfirmation
				onConfirm={onDeleteConfirm}
				onCancel={onDeleteModalClose}
				isModalVisible={isDeleteModalVisible}
			>
				<div className="flex flex-col items-center text-center gap-4">
					<SVGIcon
						icon="Error"
						className="h-12 w-auto fill-red stroke-white"
					/>
					<Text>
						{t('alert.confirmation.delete')}
						<b> {dataToDelete?.name}</b>?
					</Text>
				</div>
			</ModalConfirmation>
		</>
	);
};

export default Users;
